import { v4 as UUID } from 'uuid';
import { getLogger } from '../logging/logger';

const CN = 'FetchUtil';
const logger = getLogger(CN);

// Create a single persistent worker
const worker = new Worker(new URL('fetch.worker.js', import.meta.url));
const requests = new Map();

/**
 * Handles errors that occur in the worker thread.
 *
 * @param {Error} error - The error object from the worker thread.
 * @returns {void}
 */
worker.onerror = (error) => logger.error(`[${CN}][Worker Error]`, error);

/**
 * Handles the response message from the worker thread, resolving or rejecting the associated promise based on the response data.
 *
 * @param {MessageEvent} event - The message event from the worker thread.
 * @returns {void}
 */
worker.onmessage = (event) => {
	logger.debug(`[onResponseMessage]`, { event });

	const { requestId, error, data } = event.data;
	const resolver = requests.get(requestId);

	if (resolver) {
		if (error) {
			resolver.reject(data);
		} else {
			resolver.resolve(data);
		}
		requests.delete(requestId);
	}
};

/**
 * Fetches data from the specified URL using a worker thread.
 *
 * @param {string} url - The URL to fetch data from.
 * @param {object} [requestData] - Additional data to include in the fetch request.
 * @returns {Promise<any>} - A Promise that resolves with the fetched data or rejects with an error.
 */
export default function fetchData(url, requestData) {
	logger.log(`[fetchData]`, { url, requestData });

	return new Promise((resolve, reject) => {
		const requestId = UUID();
		requests.set(requestId, { resolve, reject });

		worker.postMessage({
			requestId,
			url,
			...requestData,
		});
	});
}
